/* eslint-disable react-hooks/exhaustive-deps */ //todo

import { useState, useEffect, lazy, Suspense } from 'react';
// import { JsonView } from './JsonView';
import {
  ProductMediaItemViewModel,
  ProductImageViewModel,
  ProductMediaType
} from '../types/generated';
import canUseDOM from '../utilities/canUseDOM';
import { ResponsiveProductImage } from './ResponsiveProductImage';

import ImageGallery from 'react-image-gallery';
import Loader from './Loader';
import ProductsILove from '../apps/product/components/ProductsILove';

const Modal = lazy(
  () =>
    import(
      /* webpackChunkName: "reactResponsiveModal" */ 'react-responsive-modal'
    )
);
const Player = lazy(
  () => import(/* webpackChunkName: "reactPlayer" */ 'react-player')
);

export interface ProductMediaGalleryProps {
  primaryImageUrl: string;
  images: ProductImageViewModel[];
  videos: ProductMediaItemViewModel[];
  thumbnailPosition?: 'top' | 'right' | 'bottom' | 'left';
  catRef?: string;
  isCssFavouritesEnabled?: boolean;
  isVariant?: boolean;
}

export interface ImageSizes {
  [size: string]: string | number;
}

const lightboxImageSizes = {
  //xs: 360,
  sm: 480,
  md: 800,
  //lg: 800,
  xl: 1200
  //  xxl: 1200
};

const imageSizes: ImageSizes = {
  //xs: 360,
  sm: 508,
  md: 338,
  //lg: 338,
  xl: 578
  //xxl: 578
};

const smallImageSizes = {
  xs: 70,
  // sm: 480,
  // md: 270,
  lg: 83
  // xl: 445,
};

interface CarouselItem {
  url: string;
  type: 'video' | 'image';
}

export default function ProductMediaGallery({
  primaryImageUrl,
  images,
  videos,
  thumbnailPosition,
  catRef,
  isCssFavouritesEnabled,
  isVariant
}: ProductMediaGalleryProps) {
  const [slideIndex, setSlideIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  function preloadPrimaryImage(imageUrl: string = '') {
    if (canUseDOM()) {
      for (const size in imageSizes) {
        const link = window.document.createElement('link');
        const head = window.document.getElementsByTagName('head')[0];

        link.rel = 'preload';
        link.as = 'image';
        link.href = encodeURI(
          `${imageUrl}?width=${imageSizes[size]}&height=${imageSizes[size]}&quality=75 1x&scale=UpscaleCanvas&anchor=MiddleCenter`
        );

        head.appendChild(link);
      }
    }
  }

  useEffect(() => {
    // console.log('checking slide index:', slideIndex)
    setSlideIndex(0);

    preloadPrimaryImage(primaryImageUrl);
  }, [primaryImageUrl, images.length, videos.length]);

  const items: CarouselItem[] = [
    {
      url: primaryImageUrl,
      type: 'image'
    },
    ...videos.map((video) => {
      const item: CarouselItem = {
        url: video.value || '',
        type: 'video'
      };
      return item;
    }),
    ...images
      .map((image) => {
        const item: CarouselItem = {
          url: image.name || '',
          type: 'image'
        };
        return item;
      })
      .filter((item) => item.url !== primaryImageUrl) // may need to add filter to remove blank URLS
  ];

  function toggleLightbox() {
    setIsLightboxOpen(!isLightboxOpen);
  }

  const pageGallery = (
    <ImageGallery
      lazyLoad={true}
      disableArrowKeys={isLightboxOpen}
      startIndex={slideIndex}
      onSlide={(currentIndex) => {
        setSlideIndex(currentIndex);
      }}
      showNav={true}
      showFullscreenButton={false}
      showBullets={false}
      showPlayButton={false}
      onClick={toggleLightbox}
      items={items.map((item) => ({
        original: item.url,
        thumbnail: item.url
      }))}
      showThumbnails={items.length > 1}
      thumbnailPosition={thumbnailPosition}
      renderThumbInner={(item) => {
        // add a check for video
        if (item.original && item.original.includes('vimeo.')) {
          return (
            <VideoThumbnail
              videoUrl={item.original}
              videoType={ProductMediaType.vimeo}
              isThumb
            />
          );
        }

        if (item.original && item.original.includes('.youtube.')) {
          return (
            <VideoThumbnail
              videoUrl={item.original}
              videoType={ProductMediaType.youTube}
              isThumb
            />
          );
        }

        return (
          <div>
            <ResponsiveProductImage
              src={item.original || ''}
              {...smallImageSizes}
            />
          </div>
        );
      }}
      renderItem={(item) => {
        // add a check for video
        if (item.original && item.original.includes('vimeo.')) {
          return (
            <div>
              <VideoThumbnail
                videoUrl={item.original}
                videoType={ProductMediaType.vimeo}
              />
              <ExpandNotice />
            </div>
          );
        }
        if (item.original && item.original.includes('.youtube.')) {
          return (
            <div>
              <VideoThumbnail
                videoUrl={item.original}
                videoType={ProductMediaType.youTube}
              />
              <ExpandNotice />
            </div>
          );
        }

        return (
          <div>
            <ResponsiveProductImage
              className="product-image"
              src={item.original || ''}
              {...imageSizes}
            />
            {!isVariant && (
              <ProductsILove
                catalogueRef={catRef}
                isEnabled={
                  isCssFavouritesEnabled ? (isVariant ? false : true) : false
                }
              />
            )}
            <ExpandNotice />
          </div>
        );
      }}
      renderLeftNav={(
        onClick: React.MouseEventHandler<HTMLElement>,
        disabled?: boolean
      ) => (
        <div className="image-gallery-custom-nav">
          <button
            className="image-gallery-custom-nav__btn image-gallery-custom-nav__btn--prev"
            disabled={disabled}
            onClick={onClick}
          >
            <span className="sr-only">Go to the previous slide</span>
          </button>
        </div>
      )}
      renderRightNav={(
        onClick: React.MouseEventHandler<HTMLElement>,
        disabled?: boolean
      ) => (
        <div className="image-gallery-custom-nav">
          <button
            className="image-gallery-custom-nav__btn image-gallery-custom-nav__btn--next"
            disabled={disabled}
            onClick={onClick}
          >
            <span className="sr-only">Go to the next slide</span>
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      {pageGallery}

      <Suspense fallback={<Loader size="xsmall" />}>
        <Modal
          open={isLightboxOpen}
          onClose={toggleLightbox}
          center
          showCloseIcon={false}
          styles={{
            modal: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              width: '94vmin',
              padding: 0
            }
          }}
        >
          <ImageGallery
            lazyLoad={true}
            startIndex={slideIndex}
            onSlide={(currentIndex) => {
              setSlideIndex(currentIndex);
            }}
            showNav={true}
            showFullscreenButton={false}
            showBullets={false}
            showPlayButton={false}
            onClick={toggleLightbox}
            showThumbnails={false}
            items={items.map((item) => ({
              original: item.url
            }))}
            renderItem={(item) => {
              // add a check for video
              if (
                item.original &&
                (item.original.includes('vimeo.') ||
                  item.original.includes('.youtube.'))
              ) {
                return (
                  <Suspense fallback={<Loader size="xsmall" />}>
                    <div className="embed-responsive embed-responsive-16by9">
                      <Player
                        className="embed-responsive-item"
                        url={item.original}
                        height="auto"
                        width="100%"
                        controls={true}
                      />
                    </div>
                  </Suspense>
                );
              }

              return (
                <ResponsiveProductImage
                  className="product-image"
                  src={item.original || ''}
                  {...lightboxImageSizes}
                />
              );
            }}
            renderThumbInner={() => null}
          />
        </Modal>
      </Suspense>
    </div>
  );
}

interface VideoThumbnailProps {
  videoUrl: string;
  videoType: ProductMediaType;
  isThumb?: boolean;
}

const VideoThumbnail = ({
  videoUrl,
  videoType,
  isThumb = false
}: VideoThumbnailProps) => {
  // if required we can determine type from the url

  const [thumbnail, setThumbnail] = useState('');

  useEffect(() => {
    if (videoType === ProductMediaType.vimeo) {
      // do vimeo thing
      if (canUseDOM()) {
        fetch(`https://vimeo.com/api/oembed.json?url=${videoUrl}`)
          .then((res) => res.json())
          .then((data) => {
            if (data && data.thumbnail_url) {
              const thumbSize = isThumb ? '_70x70.' : '_320x320.';
              const thumb = data.thumbnail_url.replace(
                /(_[0-9x]*\.)/g,
                thumbSize
              );
              setThumbnail(thumb);
            }
          });
      }
    }
  }, []); // shouldn't need to retrigger this

  if (videoType === ProductMediaType.youTube) {
    // do youTube thing
    const id = videoUrl.split('?v=')[1];

    return (
      <VideoPlaceholder
        src={`https://img.youtube.com/vi/${id}/${
          isThumb ? 'default' : 'hqdefault'
        }.jpg`}
      />
    );
  }

  return <VideoPlaceholder src={thumbnail} />;
};

const ExpandNotice = () => (
  <div className="product-image-expand">
    <span className="product-image-expand__text">Click to Expand</span>
  </div>
);

interface VideoPlaceholderProps {
  src: string;
}
const VideoPlaceholder = ({ src: thumbnailUrl }: VideoPlaceholderProps) => {
  return (
    <div className="video-placeholder">
      <div className="video-placeholder__inner">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            className="video-placeholder__image"
            alt="Video placeholder"
          />
        )}
      </div>
    </div>
  );
};
